import { END, eventChannel } from "redux-saga";
import {
  call,
  cancel,
  cancelled,
  fork,
  put,
  race,
  select,
  take,
  takeEvery,
  takeLatest,
} from "redux-saga/effects";
import { vertex360CopilotActions } from "../actions";
import ACTIONS from "../constants";
import { vertex360CopilotApi } from "../services/vertex360Copilot.services";
import { vertex360CopilotConversationApi } from "../services/vertex360CopilotConversation.services";
import {
  getVertex360CopilotConversationId,
  getVertex360CopilotHasMoreOlder,
  getVertex360CopilotIsBusy,
  getVertex360CopilotIsLoadingOlder,
  getVertex360CopilotMessages,
  getVertex360CopilotMessagesPageSize,
  getVertex360CopilotNextBeforeId,
  getVertex360CopilotStreamingMessageId,
} from "../selector/vertex360Copilot.selector";
import {
  createMessage,
  mapServerCopilotMessage,
} from "components/Vertex360Copilot/utils/vertex360CopilotChatUtils";
import { createCopilotStreamSession } from "components/Vertex360Copilot/utils/copilotStreamSession";
import { resolveVertex360CopilotErrorMessage } from "components/Vertex360Copilot/utils/vertex360CopilotErrorUtils";

/** @type {import('redux-saga/effects').Task | null} */
let activeGenerationTask = null;

function* clearVertex360CopilotBusyFlags() {
  yield put(vertex360CopilotActions.setVertex360CopilotTyping(false));
  yield put(
    vertex360CopilotActions.setVertex360CopilotStreaming({
      isStreaming: false,
      streamingMessageId: null,
    })
  );
}

/**
 * Bridge the SSE `onEvent` callback into a redux-saga channel so tokens can be
 * dispatched to the store as they arrive.
 */
function createVertex360CopilotStreamChannel(
  text,
  conversationId,
  abortController,
  clarify,
  clearActiveFilters = false
) {
  return eventChannel((emit) => {
    vertex360CopilotApi
      .sendMessageStream(text, {
        conversationId,
        clarifyMessageId: clarify?.clarifyMessageId,
        clarifySelectedOption: clarify?.clarifySelectedOption,
        clarifySelectedLabel: clarify?.clarifySelectedLabel,
        clarifyAnswers: clarify?.clarifyAnswers,
        clearActiveFilters: Boolean(clearActiveFilters),
        signal: abortController.signal,
        onEvent: (payload) => emit({ kind: "event", payload }),
      })
      .then((completeEvent) => {
        emit({ kind: "done", payload: completeEvent });
        emit(END);
      })
      .catch((error) => {
        emit({ kind: "error", error });
        emit(END);
      });

    return () => {
      try {
        abortController.abort();
      } catch {
        // no-op
      }
    };
  });
}

function* finalizeVertex360CopilotStreamSuccess() {
  yield call(clearVertex360CopilotBusyFlags);
  yield put(vertex360CopilotActions.sendVertex360CopilotMessage.success());
}

function* markVertex360CopilotStreamSuccessful(isContinuingConversation) {
  if (isContinuingConversation) {
    yield put(
      vertex360CopilotActions.setVertex360CopilotOlderPagination({
        hasMoreOlder: true,
      })
    );
  }

  yield call(finalizeVertex360CopilotStreamSuccess);
}

function* applyVertex360CopilotStreamError(error, assistantMessageId) {
  const messageId =
    assistantMessageId || (yield select(getVertex360CopilotStreamingMessageId));
  if (messageId) {
    yield put(
      vertex360CopilotActions.updateVertex360CopilotMessageContent(
        messageId,
        resolveVertex360CopilotErrorMessage(error),
        { isError: true }
      )
    );
  }
  yield call(clearVertex360CopilotBusyFlags);
}

/**
 * Apply shared stream-session effects to Redux (drawer / FAB path).
 * @param {object[]} effects
 * @param {{ assistantMessageId: string }} sessionIds mutated in place for rekeys
 */
function* applyVertex360CopilotStreamEffects(effects, sessionIds) {
  for (const effect of effects || []) {
    switch (effect.op) {
      case "conversationId":
        yield put(
          vertex360CopilotActions.setVertex360CopilotConversationId(
            effect.conversationId
          )
        );
        break;
      case "userMessageId":
        if (effect.previousMessageId) {
          yield put(
            vertex360CopilotActions.rekeyVertex360CopilotMessage(
              effect.previousMessageId,
              effect.messageId
            )
          );
        }
        break;
      case "assistantMessageId":
        if (
          effect.previousMessageId &&
          effect.previousMessageId !== effect.messageId
        ) {
          yield put(
            vertex360CopilotActions.rekeyVertex360CopilotMessage(
              effect.previousMessageId,
              effect.messageId
            )
          );
        }
        sessionIds.assistantMessageId = effect.messageId;
        break;
      case "content":
        if (effect.clearTyping) {
          yield put(vertex360CopilotActions.setVertex360CopilotTyping(false));
        }
        yield put(
          vertex360CopilotActions.updateVertex360CopilotMessageContent(
            sessionIds.assistantMessageId,
            effect.content
          )
        );
        break;
      case "render":
        yield put(
          vertex360CopilotActions.setVertex360CopilotMessageRender(
            sessionIds.assistantMessageId,
            {
              render: effect.render,
              sources: effect.sources,
              suggestions: effect.suggestions,
            }
          )
        );
        break;
      case "sources":
        yield put(
          vertex360CopilotActions.setVertex360CopilotMessageRender(
            sessionIds.assistantMessageId,
            { sources: effect.sources }
          )
        );
        break;
      case "filters":
        yield put(
          vertex360CopilotActions.setVertex360CopilotActiveFilters({
            activeFilters: effect.activeFilters,
            contextSummary: effect.contextSummary,
          })
        );
        break;
      case "degraded":
        yield put(
          vertex360CopilotActions.setVertex360CopilotMessageRender(
            sessionIds.assistantMessageId,
            {
              isReducedMode: true,
              degradeReason: effect.degradeReason || null,
              servicePath: effect.servicePath || null,
            }
          )
        );
        break;
      case "unbusyClarify":
        yield put(vertex360CopilotActions.setVertex360CopilotTyping(false));
        yield put(
          vertex360CopilotActions.setVertex360CopilotStreaming({
            isStreaming: false,
            streamingMessageId: null,
          })
        );
        break;
      case "unbusyComplete":
        // Caller marks success after applying the full effect batch.
        break;
      default:
        break;
    }
  }
}

function* runVertex360CopilotLiveStream(
  text,
  userMessageId,
  clarify,
  clearActiveFilters = false
) {
  const abortController = new AbortController();
  const conversationId = yield select(getVertex360CopilotConversationId);
  const isContinuingConversation = Boolean(conversationId);

  const assistantMessage = createMessage("assistant", "");
  yield put(
    vertex360CopilotActions.appendVertex360CopilotMessage(assistantMessage)
  );
  yield put(
    vertex360CopilotActions.setVertex360CopilotStreaming({
      isStreaming: true,
      streamingMessageId: assistantMessage.id,
    })
  );

  const channel = yield call(
    createVertex360CopilotStreamChannel,
    text,
    conversationId,
    abortController,
    clarify,
    clearActiveFilters
  );

  const session = createCopilotStreamSession({
    assistantMessageId: assistantMessage.id,
    conversationId: conversationId || null,
    userMessageId: userMessageId || null,
  });
  const sessionIds = { assistantMessageId: assistantMessage.id };

  try {
    while (true) {
      const { item, stopped } = yield race({
        item: take(channel),
        stopped: take(ACTIONS.VERTEX360_COPILOT_STOP_GENERATION),
      });

      if (stopped) {
        abortController.abort();
        yield call(clearVertex360CopilotBusyFlags);
        return false;
      }

      if (!item) {
        break;
      }

      if (item.kind === "error") {
        throw item.error;
      }

      if (item.kind === "done") {
        const { effects, error } = session.handleTransportComplete(
          item.payload || {}
        );
        if (error) throw error;
        yield call(applyVertex360CopilotStreamEffects, effects, sessionIds);
        yield call(markVertex360CopilotStreamSuccessful, isContinuingConversation);
        return true;
      }

      const { effects, error } = session.handleEvent(item.payload || {});
      if (error) throw error;
      const completed = (effects || []).some((e) => e.op === "unbusyComplete");
      yield call(applyVertex360CopilotStreamEffects, effects, sessionIds);
      if (completed) {
        yield call(markVertex360CopilotStreamSuccessful, isContinuingConversation);
        return true;
      }
    }

    if (!String(session.content || "").trim()) {
      const emptyError = new Error("Vertex360 Copilot returned an empty response.");
      emptyError.code = "AI_UNAVAILABLE";
      throw emptyError;
    }

    yield call(markVertex360CopilotStreamSuccessful, isContinuingConversation);
    return true;
  } catch (error) {
    if (error?.name === "AbortError" || (yield cancelled())) {
      yield call(clearVertex360CopilotBusyFlags);
      return false;
    }
    yield call(
      applyVertex360CopilotStreamError,
      error,
      sessionIds.assistantMessageId
    );
    throw error;
  } finally {
    channel.close();
  }
}

function* runVertex360CopilotGeneration(
  text,
  userMessageId,
  clarify,
  clearActiveFilters = false
) {
  try {
    yield put(vertex360CopilotActions.sendVertex360CopilotMessage.pending());
    yield put(vertex360CopilotActions.setVertex360CopilotTyping(true));
    return yield call(
      runVertex360CopilotLiveStream,
      text,
      userMessageId,
      clarify,
      clearActiveFilters
    );
  } catch (error) {
    if (error?.name === "AbortError" || (yield cancelled())) {
      yield call(clearVertex360CopilotBusyFlags);
      return false;
    }
    yield put(vertex360CopilotActions.sendVertex360CopilotMessage.error(error));
    return false;
  } finally {
    activeGenerationTask = null;
  }
}

function* sendVertex360CopilotMessage(action) {
  const text = String(action.payload?.text || "").trim();
  if (!text) return;

  const isBusy = yield select(getVertex360CopilotIsBusy);
  if (isBusy) return;

  const clarify = action.payload?.clarify || undefined;
  const clearActiveFilters = Boolean(action.payload?.clearActiveFilters);

  const userMessage = createMessage("user", text);
  yield put(vertex360CopilotActions.appendVertex360CopilotMessage(userMessage));

  if (activeGenerationTask) {
    yield cancel(activeGenerationTask);
    activeGenerationTask = null;
  }

  activeGenerationTask = yield fork(
    runVertex360CopilotGeneration,
    text,
    userMessage.id,
    clarify,
    clearActiveFilters
  );
}

function* handleStopVertex360CopilotGeneration() {
  if (activeGenerationTask) {
    yield cancel(activeGenerationTask);
    activeGenerationTask = null;
  }
  yield call(clearVertex360CopilotBusyFlags);
}

function* retryVertex360CopilotMessage(action) {
  const text = String(action.payload?.text || "").trim();
  if (!text) return;

  const isBusy = yield select(getVertex360CopilotIsBusy);
  if (isBusy) return;

  const messages = yield select(getVertex360CopilotMessages);
  const lastMessage = messages[messages.length - 1];
  if (lastMessage?.isError) {
    yield put(
      vertex360CopilotActions.removeVertex360CopilotMessage(lastMessage.id)
    );
  }

  if (activeGenerationTask) {
    yield cancel(activeGenerationTask);
    activeGenerationTask = null;
  }

  activeGenerationTask = yield fork(
    runVertex360CopilotGeneration,
    text,
    null,
    undefined,
    false
  );
}

function* handleResetVertex360CopilotConversation() {
  if (activeGenerationTask) {
    yield cancel(activeGenerationTask);
    activeGenerationTask = null;
  }
}

function* loadVertex360CopilotOlderMessages() {
  const conversationId = yield select(getVertex360CopilotConversationId);
  const messages = yield select(getVertex360CopilotMessages);
  const hasMoreOlder = yield select(getVertex360CopilotHasMoreOlder);
  const isLoadingOlder = yield select(getVertex360CopilotIsLoadingOlder);

  if (!conversationId || !hasMoreOlder || isLoadingOlder || !messages.length) {
    return;
  }

    const storedBeforeId = yield select(getVertex360CopilotNextBeforeId);
    const beforeId = storedBeforeId || messages[0]?.id;
    if (!beforeId) return;

    const messagesPageSize = yield select(getVertex360CopilotMessagesPageSize);

    try {
      yield put(
        vertex360CopilotActions.loadVertex360CopilotOlderMessages.pending()
      );

      const response = yield call(
        [vertex360CopilotConversationApi, vertex360CopilotConversationApi.getMessages],
        conversationId,
        {
          beforeId,
          ...(messagesPageSize ? { limit: messagesPageSize } : {}),
        }
      );

      if (response?.page_size) {
        yield put(
          vertex360CopilotActions.setVertex360CopilotMessagesPageSize(
            response.page_size
          )
        );
      }

      const older = (response?.messages || []).map(mapServerCopilotMessage);

    yield put(vertex360CopilotActions.prependVertex360CopilotMessages(older));
    yield put(
      vertex360CopilotActions.setVertex360CopilotOlderPagination({
        hasMoreOlder: Boolean(response?.has_more_older),
        nextBeforeId: response?.next_before_id
          ? String(response.next_before_id)
          : null,
      })
    );
    yield put(
      vertex360CopilotActions.loadVertex360CopilotOlderMessages.success()
    );
  } catch (error) {
    yield put(
      vertex360CopilotActions.setVertex360CopilotOlderPagination({
        hasMoreOlder: false,
      })
    );
    yield put(
      vertex360CopilotActions.loadVertex360CopilotOlderMessages.error(error)
    );
  }
}

export default function* vertex360CopilotSagas() {
  yield takeLatest(
    ACTIONS.VERTEX360_COPILOT_SEND_MESSAGE_CALL,
    sendVertex360CopilotMessage
  );
  yield takeLatest(
    ACTIONS.VERTEX360_COPILOT_LOAD_OLDER_MESSAGES_CALL,
    loadVertex360CopilotOlderMessages
  );
  yield takeEvery(
    ACTIONS.VERTEX360_COPILOT_STOP_GENERATION,
    handleStopVertex360CopilotGeneration
  );
  yield takeLatest(
    ACTIONS.VERTEX360_COPILOT_RETRY_MESSAGE_CALL,
    retryVertex360CopilotMessage
  );
  yield takeEvery(
    ACTIONS.VERTEX360_COPILOT_RESET_CONVERSATION,
    handleResetVertex360CopilotConversation
  );
}
